import { createBrowserRouter, Outlet } from 'react-router-dom';

import { AppLayout } from '@/components/layout';
import { ActivitiesPage } from '@/features/activities/pages/ActivitiesPage';
import { ActivityDetailPage } from '@/features/activities/pages/ActivityDetailPage';
import { NewActivityPage } from '@/features/activities/pages/NewActivityPage';
import { AdminDashboardPage } from '@/features/admin/pages/AdminDashboardPage';
import { AdminInvitationsPage } from '@/features/admin/pages/AdminInvitationsPage';
import { AdminInviteUserPage } from '@/features/admin/pages/AdminInviteUserPage';
import { AdminOrganizationDetailPage } from '@/features/admin/pages/AdminOrganizationDetailPage';
import { AdminOrganizationEditPage } from '@/features/admin/pages/AdminOrganizationEditPage';
import { AdminOrganizationsPage } from '@/features/admin/pages/AdminOrganizationsPage';
import { AdminUnitsPage } from '@/features/admin/pages/AdminUnitsPage';
import { AdminUserDetailPage } from '@/features/admin/pages/AdminUserDetailPage';
import { AdminUsersPage } from '@/features/admin/pages/AdminUsersPage';
import { AcceptInvitePage } from '@/features/auth/pages/AcceptInvitePage';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { ConnectPage } from '@/features/auth/pages/ConnectPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { VerifyEmailExpiredPage } from '@/features/auth/pages/VerifyEmailExpiredPage';
import { VerifyEmailPage } from '@/features/auth/pages/VerifyEmailPage';
import { BoardPage } from '@/features/board/pages/BoardPage';
import { ContractDetailPage } from '@/features/contracts/pages/ContractDetailPage';
import { ContractFormPage } from '@/features/contracts/pages/ContractFormPage';
import { ContractsPage } from '@/features/contracts/pages/ContractsPage';
import { DepartmentDetailPage } from '@/features/departments/pages/DepartmentDetailPage';
import { DepartmentsPage } from '@/features/departments/pages/DepartmentsPage';
import { TeamDetailPage } from '@/features/departments/pages/TeamDetailPage';
import { ActivityHistoryPage } from '@/features/history/pages/ActivityHistoryPage';
import { ContractHistoryPage } from '@/features/history/pages/ContractHistoryPage';
import { InvoiceHistoryPage } from '@/features/history/pages/InvoiceHistoryPage';
import { OfferHistoryPage } from '@/features/history/pages/OfferHistoryPage';
import { WeekReportHistoryPage } from '@/features/history/pages/WeekReportHistoryPage';
import { WorkPackageHistoryPage } from '@/features/history/pages/WorkPackageHistoryPage';
import { InvoiceDetailPage } from '@/features/invoices/pages/InvoiceDetailPage';
import { InvoicesPage } from '@/features/invoices/pages/InvoicesPage';
import { OfferDetailPage } from '@/features/offers/pages/OfferDetailPage';
import { OfferFormPage } from '@/features/offers/pages/OfferFormPage';
import { OffersPage } from '@/features/offers/pages/OffersPage';
import { ObjectDetailPage } from '@/features/price-lists/pages/ObjectDetailPage';
import { ObjectRegistryPage } from '@/features/price-lists/pages/ObjectRegistryPage';
import { PriceListDetailPage } from '@/features/price-lists/pages/PriceListDetailPage';
import { PriceListsPage } from '@/features/price-lists/pages/PriceListsPage';
import { AboutAppPage } from '@/features/users/pages/AboutAppPage';
import { InviteColleaguePage } from '@/features/users/pages/InviteColleaguePage';
import { OrganisationProfilePage } from '@/features/users/pages/OrganisationProfilePage';
import { PartnerLinkingPage } from '@/features/users/pages/PartnerLinkingPage';
import { RolesDeactivationPage } from '@/features/users/pages/RolesDeactivationPage';
import { WeekReportCorrectionPage } from '@/features/week-reports/pages/WeekReportCorrectionPage';
import { WeekReportDetailPage } from '@/features/week-reports/pages/WeekReportDetailPage';
import { WeekReportsPage } from '@/features/week-reports/pages/WeekReportsPage';
import { WorkPackageDetailPage } from '@/features/work-packages/pages/WorkPackageDetailPage';
import { WorkPackageFormPage } from '@/features/work-packages/pages/WorkPackageFormPage';
import { WorkPackagesPage } from '@/features/work-packages/pages/WorkPackagesPage';
import { AppErrorPage } from '@/pages/AppErrorPage';
import { AppNotFoundPage } from '@/pages/AppNotFoundPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ErrorPage } from '@/pages/ErrorPage';
import { ProtectedRoute } from '@/router/ProtectedRoute';
import { AppAdminRoute, OrgScopedRoute } from '@/router/RoleRoutes';
import { ScrollToTop } from '@/router/ScrollToTop';

export const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollToTop />
        <Outlet />
      </>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/login',
        element: <LoginPage />
      },
      {
        path: '/register',
        element: <RegisterPage />
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />
      },
      {
        path: '/verify-email',
        element: <VerifyEmailPage />
      },
      {
        path: '/verify-email/expired',
        element: <VerifyEmailExpiredPage />
      },
      {
        path: '/accept-invite',
        element: <AcceptInvitePage />
      },
      {
        path: '/connect',
        element: <ConnectPage />
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            // Izvan AppLayout-a namjerno — dok mustChangePassword vrijedi, backend
            // blokira (403) sve ostale endpointe, pa nema smisla renderirati
            // sidebar/widgete koji bi samo tiho pucali u pozadini.
            path: '/change-password',
            element: <ChangePasswordPage />
          },
          {
            element: <AppLayout />,
            children: [
              {
                element: <OrgScopedRoute />,
                errorElement: <AppErrorPage />,
                children: [
                  {
                    path: '/',
                    element: <DashboardPage />,
                    handle: { breadcrumbKey: 'nav.dashboard' }
                  },
                  {
                    path: '/board',
                    element: <BoardPage />,
                    handle: { breadcrumbKey: 'nav.board' }
                  },
                  {
                    path: '/activities',
                    element: <ActivitiesPage />,
                    handle: { breadcrumbKey: 'nav.activities' }
                  },
                  {
                    path: '/activities/new',
                    element: <NewActivityPage />,
                    handle: { breadcrumbKey: 'nav.activities' }
                  },
                  {
                    path: '/activities/:id',
                    element: <ActivityDetailPage />,
                    handle: { breadcrumbKey: 'nav.activities' }
                  },
                  {
                    path: '/activities/:id/edit',
                    element: <NewActivityPage />,
                    handle: { breadcrumbKey: 'nav.activities' }
                  },
                  {
                    path: '/activities/:id/history',
                    element: <ActivityHistoryPage />,
                    handle: { breadcrumbKey: 'nav.activities' }
                  },
                  {
                    path: '/offers',
                    element: <OffersPage />,
                    handle: { breadcrumbKey: 'nav.offers' }
                  },
                  {
                    path: '/offers/new',
                    element: <OfferFormPage />,
                    handle: { breadcrumbKey: 'nav.offers' }
                  },
                  {
                    path: '/offers/:id',
                    element: <OfferDetailPage />,
                    handle: { breadcrumbKey: 'nav.offers' }
                  },
                  {
                    path: '/offers/:id/edit',
                    element: <OfferFormPage />,
                    handle: { breadcrumbKey: 'nav.offers' }
                  },
                  {
                    path: '/offers/:id/history',
                    element: <OfferHistoryPage />,
                    handle: { breadcrumbKey: 'nav.offers' }
                  },
                  {
                    path: '/work-packages',
                    element: <WorkPackagesPage />,
                    handle: { breadcrumbKey: 'nav.workPackages' }
                  },
                  {
                    path: '/work-packages/new',
                    element: <WorkPackageFormPage />,
                    handle: { breadcrumbKey: 'nav.workPackages' }
                  },
                  {
                    path: '/work-packages/:id',
                    element: <WorkPackageDetailPage />,
                    handle: { breadcrumbKey: 'nav.workPackages' }
                  },
                  {
                    path: '/work-packages/:id/edit',
                    element: <WorkPackageFormPage />,
                    handle: { breadcrumbKey: 'nav.workPackages' }
                  },
                  {
                    path: '/work-packages/:id/history',
                    element: <WorkPackageHistoryPage />,
                    handle: { breadcrumbKey: 'nav.workPackages' }
                  },
                  {
                    path: '/contracts',
                    element: <ContractsPage />,
                    handle: { breadcrumbKey: 'nav.contracts' }
                  },
                  {
                    path: '/contracts/new',
                    element: <ContractFormPage />,
                    handle: { breadcrumbKey: 'nav.contracts' }
                  },
                  {
                    path: '/contracts/:id',
                    element: <ContractDetailPage />,
                    handle: { breadcrumbKey: 'nav.contracts' }
                  },
                  {
                    path: '/contracts/:id/edit',
                    element: <ContractFormPage />,
                    handle: { breadcrumbKey: 'nav.contracts' }
                  },
                  {
                    path: '/contracts/:id/history',
                    element: <ContractHistoryPage />,
                    handle: { breadcrumbKey: 'nav.contracts' }
                  },
                  {
                    path: '/weekly-reports',
                    element: <WeekReportsPage />,
                    handle: { breadcrumbKey: 'nav.weeklyReports' }
                  },
                  {
                    path: '/weekly-reports/:id',
                    element: <WeekReportDetailPage />,
                    handle: { breadcrumbKey: 'nav.weeklyReports' }
                  },
                  {
                    path: '/weekly-reports/:id/correction',
                    element: <WeekReportCorrectionPage />,
                    handle: { breadcrumbKey: 'nav.weeklyReports' }
                  },
                  {
                    path: '/weekly-reports/:id/history',
                    element: <WeekReportHistoryPage />,
                    handle: { breadcrumbKey: 'nav.weeklyReports' }
                  },
                  {
                    path: '/invoices',
                    element: <InvoicesPage />,
                    handle: { breadcrumbKey: 'nav.invoices' }
                  },
                  {
                    path: '/invoices/:id',
                    element: <InvoiceDetailPage />,
                    handle: { breadcrumbKey: 'nav.invoices' }
                  },
                  {
                    path: '/invoices/:id/history',
                    element: <InvoiceHistoryPage />,
                    handle: { breadcrumbKey: 'nav.invoices' }
                  },
                  {
                    path: '/templates/price-lists',
                    element: <PriceListsPage />,
                    handle: { breadcrumbKey: ['nav.templates', 'userNav.priceLists'] }
                  },
                  {
                    path: '/templates/price-lists/:id',
                    element: <PriceListDetailPage />,
                    handle: { breadcrumbKey: ['nav.templates', 'userNav.priceLists'] }
                  },
                  {
                    path: '/templates/object-registry',
                    element: <ObjectRegistryPage />,
                    handle: { breadcrumbKey: ['nav.templates', 'userNav.objectRegistry'] }
                  },
                  {
                    path: '/templates/object-registry/:id',
                    element: <ObjectDetailPage />,
                    handle: { breadcrumbKey: ['nav.templates', 'userNav.objectRegistry'] }
                  },
                  {
                    path: '/users/invite',
                    element: <InviteColleaguePage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsInvite'] }
                  },
                  {
                    path: '/users/roles',
                    element: <RolesDeactivationPage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsUsers'] }
                  },
                  {
                    path: '/partners',
                    element: <PartnerLinkingPage />,
                    handle: { breadcrumbKey: ['nav.partners'] }
                  },
                  {
                    path: '/users/org-profile',
                    element: <OrganisationProfilePage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsOrgProfile'] }
                  },
                  {
                    path: '/users/about',
                    element: <AboutAppPage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsAbout'] }
                  },
                  {
                    path: '/users/departments',
                    element: <DepartmentsPage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsDepartments'] }
                  },
                  {
                    path: '/users/departments/:departmentId',
                    element: <DepartmentDetailPage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsDepartments'] }
                  },
                  {
                    path: '/users/departments/:departmentId/teams/:teamId',
                    element: <TeamDetailPage />,
                    handle: { breadcrumbKey: ['nav.settings', 'nav.settingsDepartments'] }
                  },
                  {
                    path: '*',
                    element: <AppNotFoundPage />
                  }
                ]
              },
              {
                element: <AppAdminRoute />,
                errorElement: <AppErrorPage />,
                children: [
                  {
                    path: '/admin',
                    element: <AdminDashboardPage />,
                    handle: { breadcrumbKey: 'adminNav.dashboard' }
                  },
                  {
                    path: '/admin/organizations',
                    element: <AdminOrganizationsPage />,
                    handle: { breadcrumbKey: 'adminNav.organizations' }
                  },
                  {
                    path: '/admin/organizations/:id',
                    element: <AdminOrganizationDetailPage />,
                    handle: { breadcrumbKey: 'adminNav.organizations' }
                  },
                  {
                    path: '/admin/organizations/:id/edit',
                    element: <AdminOrganizationEditPage />,
                    handle: { breadcrumbKey: 'adminNav.organizations' }
                  },
                  {
                    path: '/admin/users',
                    element: <AdminUsersPage />,
                    handle: { breadcrumbKey: 'adminNav.users' }
                  },
                  {
                    path: '/admin/users/:id',
                    element: <AdminUserDetailPage />,
                    handle: { breadcrumbKey: 'adminNav.users' }
                  },
                  {
                    path: '/admin/invitations',
                    element: <AdminInvitationsPage />,
                    handle: { breadcrumbKey: 'adminNav.invitations' }
                  },
                  {
                    path: '/admin/units',
                    element: <AdminUnitsPage />,
                    handle: { breadcrumbKey: 'adminNav.units' }
                  },
                  {
                    path: '/admin/invitations/new',
                    element: <AdminInviteUserPage />,
                    handle: {
                      breadcrumbKey: ['adminNav.invitations', 'adminInvitations.newInvitationCrumb']
                    }
                  },
                  {
                    path: '*',
                    element: <AppNotFoundPage />
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
]);
